import { useEffect, useState } from "react";
import { Input, Select, Table } from "antd";
import { useQuery } from "react-query";
import { useNavigate } from "react-router";
import { getWithdrawals, getWithdrawalSummary } from "../../api/withdrawal";
import { getCustomerId } from "../shared/CustomerLink";
import { withdrawalColumns } from "./column";
import NoAccess from "../shared/NoAccess";
import { usePermissionEffect } from "../../utils/usePermissionEffect";

const naira = (n) => `₦${Number(n || 0).toLocaleString()}`;

const Withdrawals = () => {
  const navigate = useNavigate();
  const { getModulePermission } = usePermissionEffect();
  const canView = getModulePermission("Withdrawal", "canView");
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState("fiat");
  const [status, setStatus] = useState();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // debounce search so we don't query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isFetching } = useQuery(
    ["withdrawals", page, kind, status, search],
    () => getWithdrawals({ page, limit: 20, kind, status, search }),
    { keepPreviousData: true, enabled: canView },
  );
  const { data: summary } = useQuery("withdrawal-summary", getWithdrawalSummary, { enabled: canView });

  const cards = [
    ["Total paid out", naira(summary?.completedAmount)],
    ["Pending", `${summary?.pendingCount || 0} · ${naira(summary?.pendingAmount)}`],
    ["Last 24 hours", `${summary?.last24hCount || 0} · ${naira(summary?.last24hAmount)}`],
    ["Failed", summary?.failedCount || 0],
  ];

  if (!canView) return <NoAccess what="withdrawals" />;

  return (
    <div className="w-full h-full pt-5 px-5 pb-40 space-y-6 font-dmsans">
      <div className="font-bold text-[22px] text-[#070E05]">Withdrawals</div>

      <div className="grid grid-cols-4 gap-4">
        {cards.map(([title, value]) => (
          <div key={title} className="bg-white rounded-lg px-[28px] py-[18px]">
            <div className="text-[12px] text-[#00000080]">{title}</div>
            <div className="text-[22px] font-bold text-[#14142B] mt-2">{value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-4 bg-white rounded-lg p-5">
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <div className="font-bold text-[18px] text-[#070E05]">
            Outgoing withdrawals
          </div>
          <div className="flex gap-3">
            <Select
              value={kind}
              onChange={(v) => {
                setKind(v);
                setPage(1);
              }}
              className="w-[160px]"
              options={[
                { value: "fiat", label: "Bank payouts" },
                { value: "crypto", label: "Crypto sends" },
                { value: "all", label: "All" },
              ]}
            />
            <Select
              allowClear
              placeholder="Any status"
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              className="w-[140px]"
              options={["pending", "completed", "failed", "refunded"].map((s) => ({
                value: s,
                label: s[0].toUpperCase() + s.slice(1),
              }))}
            />
            <Input.Search
              allowClear
              placeholder="Name, email, account no, reference"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-[300px]"
            />
          </div>
        </div>
        <div className="text-[13px] text-[#00000080]">
          Click a row to open the customer's details.
        </div>
        <Table
          rowKey="_id"
          loading={isFetching}
          dataSource={data?.withdrawals || []}
          columns={withdrawalColumns()}
          rowClassName="cursor-pointer hover:bg-gray-50"
          onRow={(record) => ({
            onClick: () => {
              const id = getCustomerId(record);
              if (id) navigate(`/customers/${id}`);
            },
          })}
          pagination={{
            current: page,
            pageSize: 20,
            total: data?.total || 0,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </div>
    </div>
  );
};

export default Withdrawals;
